import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { AnimatePresence, motion } from "motion/react";
import { Check, ChevronDown } from "@/components/ui/icons";
import { useState, useRef, useEffect, type ReactNode } from "react";

interface FilterOption {
  value: string;
  label: string;
}

interface FilterDropdownProps {
  label: string;
  value: string;
  options: FilterOption[];
  onValueChange: (value: string) => void;
  allLabel?: string;
}

export function FilterDropdown({
  label,
  value,
  options,
  onValueChange,
  allLabel = "All",
}: FilterDropdownProps): ReactNode {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const prevOpenRef = useRef(false);
  const isActive = value !== "all";

  const selectedLabel =
    value === "all"
      ? allLabel
      : options.find((o) => o.value === value)?.label ?? value;

  const handleSelect = (val: string) => {
    onValueChange(val);
    setOpen(false);
  };

  useEffect(() => {
    if (prevOpenRef.current && !open) triggerRef.current?.focus();
    prevOpenRef.current = open;
  }, [open]);

  const allItems = [
    { value: "all", label: allLabel },
    ...options,
  ];

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          ref={triggerRef}
          className={`flex items-center gap-1.5 rounded-full border bg-transparent px-4 py-2 text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-accent/40 hover:border-accent hover:bg-accent/10 data-[state=open]:border-accent data-[state=open]:bg-accent/10 ${
            isActive ? "border-accent bg-accent/10" : "border-border"
          }`}
        >
          <span
            className={`text-xs ${isActive ? "text-accent/70" : "text-muted-foreground"}`}
          >
            {label}:
          </span>
          <span
            className={`font-medium ${isActive ? "text-accent" : "text-foreground"}`}
          >
            {selectedLabel}
          </span>
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform duration-200 ${
              isActive ? "text-accent" : "text-muted-foreground"
            } ${open ? "rotate-180" : ""}`}
          />
        </button>
      </DropdownMenu.Trigger>

      <AnimatePresence>
        {open && (
          <DropdownMenu.Portal forceMount>
            <DropdownMenu.Content
              asChild
              sideOffset={6}
              align="center"
              onCloseAutoFocus={() => triggerRef.current?.focus()}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.92, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: -4 }}
                transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                style={{
                  transformOrigin:
                    "var(--radix-dropdown-menu-content-transform-origin)",
                }}
                className="z-50 min-w-[var(--radix-dropdown-menu-trigger-width)] max-h-[300px] overflow-y-auto rounded-2xl border border-border bg-popover p-2 shadow-lg"
              >
                {allItems.map((item, i) => (
                  <DropdownMenu.Item
                    key={item.value}
                    onSelect={() => handleSelect(item.value)}
                    className={`relative flex w-full cursor-pointer items-center justify-center rounded-full px-4 py-2.5 text-sm outline-none select-none transition-colors duration-100 hover:bg-accent hover:text-white focus:bg-accent focus:text-white ${
                      value === item.value
                        ? "font-medium text-accent"
                        : "text-foreground"
                    } ${i === 0 ? "mb-1.5" : ""}`}
                  >
                    {item.label}
                    {value === item.value && (
                      <Check className="absolute right-3 h-3.5 w-3.5 shrink-0" />
                    )}
                  </DropdownMenu.Item>
                ))}
              </motion.div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        )}
      </AnimatePresence>
    </DropdownMenu.Root>
  );
}
